import { useContext } from 'react';
import { SEAT_TIMER_SIZE } from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import { TableIdContext } from 'contexts/TableIdContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import { useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';
import { isHighWinner, isLowWinner, isWinner } from 'utils/functions';
import { shouldPlayTableSfx } from 'utils/tableSfx';
import { APP_DOMAIN } from 'utils/urls';

import { useSeatStreakFrame } from 'components/Streaks/state/useSeatStreakFrame';
import SeatTimer from 'components/Timer/SeatTimer';
import { isSeatPrompted } from 'components/UserActions/MultiRunActions/multiRunPhase';
import { voteWindowSecs } from 'components/UserActions/MultiRunActions/voteWindow';

import { BountyWinner } from './BountyWinner';
import { Cash } from './Cash';
import ChargeContainer from './ChargeContainer';
import Player from './Player';
import { WinnerMsg } from './WinnerMsg';

import 'components/Seat/styles/shared.scss';

type SeatWithPlayerPropType = {
  name?: string;
  player?: any;
  bet?: any;
  stack: string;
  turn?: boolean;
  avatar: string;
  seatId: number;
  currentPlayerSeatId: number;
  action?: string;
  isFaceCardUp: boolean;
  totalBounty?: number;
  totalHandBounty: number;
  cardPosition?: any;
  /** Hide the Cash (name + balance) info — used when parent renders it separately */
  hideInfo?: boolean;
  /** Hide the WinnerMsg pill — used when parent renders it in a custom location */
  hideWinnerMsg?: boolean;
};

export const SeatWithPlayer = ({
  stack,
  avatar,
  seatId,
  currentPlayerSeatId,
  isFaceCardUp,
  totalHandBounty,
  hideInfo = false,
  hideWinnerMsg = false
}: SeatWithPlayerPropType) => {
  const { isTimerOff } = useContext(GameContext);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const isCurrentPlayerSeat = seatId === currentPlayerSeatId;
  // Scopes the cross-table win claim so two tables resolving together each
  // get their own cue rather than one swallowing the other.
  const winCueTableId = useContext(TableIdContext);
  const { muteOtherPlayers, muteOtherPlayersSetByUser } = useContext(TableSettingsContext);
  // Win cues belong to THIS seat's player. Unity gates PLAYERWINS on
  // IsPOVPlayer() (UIPlayer.cs:825), so another player's win is silent while
  // opponents are muted — including a win by everyone folding, which is exactly
  // the hand-resolution case the ungated version leaked through.
  const winSoundEnabled = shouldPlayTableSfx({
    origin: 'player',
    actorSeatId: seatId,
    currentPlayerSeatId,
    muteOtherPlayersSounds: muteOtherPlayers,
    muteOtherPlayersIsExplicit: muteOtherPlayersSetByUser
  });
  const mediaUrl = `https://media.${APP_DOMAIN}/images/`;

  const game = useTableSelector((table) => table.game);
  const { pots, showdown } = game;
  const players = useTableSelector((table) => table.players);
  const startTurn = useTableSelector((table) => table.gameState.startTurn);
  const chargedFlag = useAppSelector((state) => state.user.playerCharged);
  const hand = useTableSelector((table) => table.game.hand);
  const isMultiRunActive = useTableSelector((table) => table.currentPlayer.isMultiRunActive);
  const isCurrentWinner = isWinner(pots, showdown, seatId);
  const isCurrentHighWinner = isHighWinner(pots, showdown, seatId);
  const isCurrentLowWinner = isLowWinner(pots, showdown, seatId);

  const timerSpeed = useTableSelector((table) => table.game.speed);
  const time = useTableSelector((table) => table.game.time);
  const lastmove = useTableSelector((table) => table.game.lastmove);
  const languageKey = useTableSelector((table) => table.game.languageKey);
  const isTurn = seatId.toString() === game.move && startTurn;

  // EN-663: the multi-run vote window runs on the player's OWN action timer —
  // the ring they already read on a turn, counting `multiRunVoteSecs` instead
  // of `game.speed`. The vote panel used to carry a second, half-size dial of
  // its own beside the prompt; two clocks for one question, and in portrait it
  // cost the vote buttons the width they needed. Scoped to this seat because
  // the window is this player's own decision to make.
  // EN-663 AC-2.2 / AC-3.1: the multi-run decision uses STANDARD TURN
  // PRESENTATION — the highlighted avatar and its countdown — and that is the
  // table's only signal of whose decision it is now that the status lines are
  // gone (AC-5.1). So the ring follows the seat BEING ASKED, for everyone
  // watching, exactly as `isTurn` below does for a normal turn.
  //
  // It used to be scoped to `isCurrentPlayerSeat`, which was right when the
  // window opened for every eligible seat simultaneously and the panel carried
  // its own prompt copy: there was nothing to point at. Under the two-phase
  // offer there is — phase 1 is one named player's decision — and a highlight
  // only its owner can see communicates nothing to the other eight.
  const isVoting = isSeatPrompted(game, seatId);
  // While a vote window is open the table's clock is the vote's. `move` and
  // `startTurn` can still name the last betting actor, and no betting decision
  // is pending once the all-in is called, so a betting turn is not one then: it
  // must neither start a second clock beside the prompted seat's nor beep.
  const isBettingTurn = isTurn && !String(game.runs ?? '').includes('v');

  // The ring countdown on this seat (a betting turn or the multi-run vote).
  const isStartProgress =
    !isHandHistorySnapshot &&
    (isVoting ||
      (isBettingTurn &&
        +hand < 12 &&
        +hand > 4 &&
        !isMultiRunActive &&
        !(isCurrentPlayerSeat && isTimerOff)));
  // EN-11164: the streak frame this seat's player wears (ops.streaks).
  const streakFrame = useSeatStreakFrame(players?.[seatId]?.GUID);

  return (
    <div style={{ position: 'relative', zIndex: 1 }}>
      {!hideInfo && (
        <Cash
          cash={+stack}
          player={players[seatId].displayName}
          playerGUID={players[seatId].GUID}
          isCharged={chargedFlag && isFaceCardUp}
          seatId={seatId}
        />
      )}
      {/* Winner pill — shown in both portrait and landscape unless the parent
          opts out (e.g. current-player portrait renders it above the cards). */}
      {!hideWinnerMsg &&
        (isCurrentLowWinner && languageKey !== 'GAME_MSG_ALLFOLD' ? (
          <WinnerMsg
            backgroundColor={'#628B48'}
            message='Low Winner'
            soundEnabled={winSoundEnabled}
            isOwnWin={isCurrentPlayerSeat}
            gameID={String(winCueTableId)}
          />
        ) : isCurrentHighWinner ? (
          <WinnerMsg
            backgroundColor={'#628B48'}
            soundEnabled={winSoundEnabled}
            isOwnWin={isCurrentPlayerSeat}
            gameID={String(winCueTableId)}
          />
        ) : (
          <></>
        ))}
      {false && <ChargeContainer />}

      {game.bustedPlayerCount > 0 &&
      game.guidWinner.includes(seatId) &&
      +hand > 13 &&
      game.gameType === 'Bounty' ? (
        <BountyWinner
          totalHandBounty={totalHandBounty}
          backgroundColor={'#628B48'}
          soundEnabled={winSoundEnabled}
        />
      ) : (
        ''
      )}

      <SeatTimer
        start={lastmove}
        current={time}
        target={isVoting ? voteWindowSecs(game) : timerSpeed}
        // EN-10920: the countdown beeps are the local player's own — their
        // betting turn or their open vote. The ring above runs on every seat
        // being asked, for everyone watching; the beeps never do.
        ownsWarningCue={isCurrentPlayerSeat && (isVoting || isBettingTurn)}
        isStartProgress={isStartProgress}
        size={SEAT_TIMER_SIZE}>
        <Player
          currentPlayerSeatId={currentPlayerSeatId}
          seatId={seatId}
          avatar={mediaUrl.concat(avatar)}
          isCurrentWinner={isCurrentWinner}
          streakFrame={streakFrame}
          // The turn timer's ring sits where the frame's band is (EN-11164).
          hideStreakFrame={isStartProgress}
        />
      </SeatTimer>
    </div>
  );
};
